// One soft-shadow texture, shared by every actor in the scene.
//
// Drawn as a tinted Sprite rather than as a per-actor Graphics on purpose. A
// bird's contact shadow is a flat ellipse whose only variables are its two
// half-extents, so the expensive part — the falloff — is identical for all of
// them and belongs in a single texture. Re-tessellating a Graphics per bird per
// frame is what this avoids.

import { Texture } from 'pixi.js';

/** Edge length of the generated patch. Resolution only — nothing scales to it. */
export const SHADOW_TEXTURE_SIZE = 96;

let cached: Texture | null = null;

/**
 * A radial falloff, transparent at the rim so the patch has no visible edge.
 * Baked once and never regenerated.
 *
 * Returns `Texture.EMPTY` where there is no 2D canvas to rasterise into, so a
 * headless or unit-test environment degrades to an invisible shadow rather
 * than throwing mid-frame.
 */
export function shadowTexture(): Texture {
  if (cached) return cached;
  if (typeof document === 'undefined') return Texture.EMPTY;

  const size = SHADOW_TEXTURE_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Texture.EMPTY;

  // Stopped short of 1.0 so the outermost pixels are already transparent: a
  // gradient that reaches full transparency at the rim leaves a faint ring
  // where the sprite's bilinear filter samples just inside the edge.
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, 'rgba(0,0,0,1)');
  gradient.addColorStop(0.45, 'rgba(0,0,0,0.85)');
  gradient.addColorStop(0.78, 'rgba(0,0,0,0.32)');
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  cached = Texture.from(canvas);
  return cached;
}

/**
 * Scale that maps the shared patch onto a contact shadow. The patch inscribes
 * its ellipse in `SHADOW_TEXTURE_SIZE`, so dividing a desired half-extent by
 * half the texture lands the falloff edge exactly on that half-extent.
 */
export function shadowScale(rx: number, ry: number): { x: number; y: number } {
  const half = SHADOW_TEXTURE_SIZE / 2;
  return { x: rx / half, y: ry / half };
}
